import React, { useState, useEffect, createContext, useContext } from 'react'
import { useAuth } from './useAuth'
import axios from 'axios'
import { API_BASE_URL } from '../utils/api'

interface BrokerContext {
  brokerId: number
  brokerName: string
  brokerEmail: string
  collaboratorId: number
  status: string
  accessLevel: string
  lastAccess: string
  collaborationTitle?: string
}

interface ActiveBrokerContext {
  brokerId: number
  brokerName: string
  collaboratorId: number
  accessLevel: string
  permissions: any
  brokerInfo?: {
    first_name: string
    last_name: string
    email: string
    phone: string
  }
  collaboratorInfo?: {
    collaboration_title: string
    joined_at: string
    last_active_at: string
  }
}

interface BrokerContextState {
  availableBrokers: BrokerContext[]
  activeBrokerContext: ActiveBrokerContext | null
  loading: boolean
  error: string | null
  switchBrokerContext: (brokerId: number) => Promise<void>
  refreshContext: () => Promise<void>
  logoutFromContext: () => Promise<void>
  isMultiBrokerEnabled: boolean
}

const BrokerContextContext = createContext<BrokerContextState | null>(null)

export const useBrokerContext = () => {
  const context = useContext(BrokerContextContext)
  if (!context) {
    throw new Error('useBrokerContext must be used within a BrokerContextProvider')
  }
  return context
}

export const useBrokerContextHook = () => {
  const { user } = useAuth()
  const [availableBrokers, setAvailableBrokers] = useState<BrokerContext[]>([])
  const [activeBrokerContext, setActiveBrokerContext] = useState<ActiveBrokerContext | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isMultiBrokerEnabled = user?.role === 'collaborator'

  useEffect(() => {
    if (isMultiBrokerEnabled) {
      refreshContext()
    }
  }, [user, isMultiBrokerEnabled])

  const fetchAvailableBrokers = async (): Promise<BrokerContext[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/broker-context/available`)
      return response.data.availableBrokers || []
    } catch (error: any) {
      console.error('Error fetching available brokers:', error)
      throw new Error('Failed to load available broker contexts')
    }
  }

  const fetchCurrentContext = async (): Promise<ActiveBrokerContext | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/broker-context/current`)
      return response.data.activeBrokerContext
    } catch (error: any) {
      // It's ok if there's no current context
      return null
    }
  }

  const refreshContext = async () => {
    if (!isMultiBrokerEnabled) return

    try {
      setLoading(true)
      setError(null)

      const [brokers, currentContext] = await Promise.all([
        fetchAvailableBrokers(),
        fetchCurrentContext()
      ])

      setAvailableBrokers(brokers)
      setActiveBrokerContext(currentContext)

      // Auto-switch if there's only one broker and no active context
      if (brokers.length === 1 && !currentContext) {
        await switchBrokerContext(brokers[0].brokerId)
      }

    } catch (err: any) {
      setError(err.message || 'Failed to refresh broker context')
    } finally {
      setLoading(false)
    }
  }

  const switchBrokerContext = async (brokerId: number) => {
    try {
      setLoading(true)
      setError(null)

      const response = await axios.post(`${API_BASE_URL}/api/broker-context/switch/${brokerId}`)
      setActiveBrokerContext(response.data.activeBrokerContext)

      // Update the user's current context in local storage for persistence
      const updatedUser = { ...user, current_broker_context: brokerId }
      localStorage.setItem('user', JSON.stringify(updatedUser))

    } catch (error: any) {
      console.error('Error switching broker context:', error)
      const errorMessage = error.response?.data?.message || 'Failed to switch broker context'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const logoutFromContext = async () => {
    try {
      setLoading(true)
      setError(null)

      await axios.post(`${API_BASE_URL}/api/broker-context/logout`)
      setActiveBrokerContext(null)

      // Update the user's current context in local storage
      if (user) {
        const updatedUser = { ...user, current_broker_context: null }
        localStorage.setItem('user', JSON.stringify(updatedUser))
      }

    } catch (error: any) {
      console.error('Error logging out from broker context:', error)
      const errorMessage = error.response?.data?.message || 'Failed to logout from broker context'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return {
    availableBrokers,
    activeBrokerContext,
    loading,
    error,
    switchBrokerContext,
    refreshContext,
    logoutFromContext,
    isMultiBrokerEnabled
  }
}

// Context Provider Component
export const BrokerContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useBrokerContextHook()
  
  return (
    <BrokerContextContext.Provider value={contextValue}>
      {children}
    </BrokerContextContext.Provider>
  )
}

// HOC for components that need broker context
export const withBrokerContext = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => {
  return (props: P) => {
    const brokerContext = useBrokerContext()
    
    // Add broker context to component props
    const enhancedProps = {
      ...props,
      brokerContext
    } as P

    return <Component {...enhancedProps} />
  }
}

// Utility function to get current broker ID for API calls
export const getCurrentBrokerId = (): number | null => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user.current_broker_context || null
  } catch {
    return null
  }
}

// Utility function to make API calls with automatic broker context
export const makeSecureApiCall = async (endpoint: string, options: any = {}) => {
  const brokerId = getCurrentBrokerId()
  
  // Add broker context to query params for collaborators
  if (brokerId) {
    const url = new URL(endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`)
    url.searchParams.append('brokerId', brokerId.toString())
    endpoint = url.toString()
  }

  return axios({
    url: endpoint,
    ...options,
    headers: {
      ...options.headers,
      'X-Broker-Context': brokerId || '',
    }
  })
}
